import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { h3, h5, paragraphLarge, paragraphMedium, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CtaLinkStyles from '@stories/Components/Buttons/CtaLink/CtaLink.styles';
import FieldSharedStyles from '@stories/Components/FormFields/FieldShared.styles';
import DropdownStyles from '@stories/Components/Inputs/Dropdown/Dropdown.styles';
import RadioBtnStyles from '@stories/Components/Inputs/RadioBtn/RadioBtn.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { m } from 'framer-motion';
import { transparentize } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  background-color: ${brand.primary.blueGrey};
`;
const Layout = styled.div `
  display: grid;
  gap: ${fluid(30, 90)};

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: 35fr 65fr;
  }
`;
const SectionTitle = styled.div `
  ${h3}

  margin-bottom: ${fluid(28, 40)};
`;
const EventType = styled.div `
  text-transform: uppercase;
  ${paragraphSmall}
  letter-spacing: 1px;
  color: ${transparentize(0.5, brand.base.blue)};
  margin: 0;
  margin-bottom: 24px;
`;
const LeftContent = styled.div `
  display: flex;
  flex-direction: column;
  @media ${from(Device.TabletLarge)} {
    position: sticky;
    top: var(--sitePadding);
    height: max-content;
    max-height: 100%;
  }
`;
const Title = styled.div `
  ${paragraphLarge}
  margin: 0;
  margin-bottom: 24px;
`;
const InPartnershipWith = styled.div `
  ${paragraphSmall}
  color: ${transparentize(0.5, brand.base.blue)};
  margin: 0;
`;
const HR = styled.hr `
  margin: 32px 0px;
  border: 0;
  background-color: ${brand.secondary.blueGrey};
  height: 1px;
  width: 100%;

  @media ${until(Device.TabletLarge)} {
    order: 3;
    margin-bottom: 12px;
  }
`;
const DetailsList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const Details = styled.div `
  display: flex;
  flex-direction: column;
  ${paragraphSmall}
  margin: 0;

  span:first-of-type {
    color: ${transparentize(0.5, brand.base.blue)};
  }
`;
const FormAccordion = styled.div `
  display: flex;
  flex-direction: column;

  ${() => FormBase}:first-child {
    padding-top: 0px;
  }
`;
const AlternativeContent = styled.div `
  display: flex;
  flex-direction: column;

  ${() => FormBase}:first-child {
    padding-top: 0px;
  }
`;
const FormContent = styled(m.div).attrs(({ isActive }) => ({
    initial: 'hidden',
    animate: isActive ? 'visible' : 'hidden',
    exit: 'hidden',
    variants: {
        hidden: {
            height: '0px',
        },
        visible: {
            height: 'auto',
        },
    },
    transition: {
        bounce: 0,
    },
})) `
  overflow: hidden;
  padding: 0 5px;
  margin: 0 -5px;
`;
const FormBase = styled.div `
  display: flex;
  flex-direction: column;
  padding: 32px 0px;
  border-bottom: 1px solid ${transparentize(0.5, brand.primary.blue)};

  transition: opacity 0.25s ease-in-out;
  [data-form-disabled='true'] & {
    opacity: 0.3;
  }

  ${DropdownStyles.Select} {
    background-color: ${brand.white};

    &[data-has-value='false'],
    option:first-of-type {
      color: ${transparentize(0.5, brand.base.blue)};
    }

    option {
      color: ${brand.base.blue};
    }
  }
`;
const FormContentInner = styled.div `
  padding-top: 32px;
`;
const FormStepTitle = styled.button `
  ${ButtonReset};
  ${h5};

  margin: 0;
  color: ${transparentize(0.5, brand.base.blue)};
  user-select: none;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  text-align: left;

  span:nth-of-type(2) {
    margin: 0 0 -3px auto;
    font-size: ${fluid(16, 18)};
    line-height: 1;
    color: ${brand.base.blue};
  }

  svg {
    display: block;
    color: ${brand.primary.orange};
    width: ${fluid(16, 24)};
  }
`;
const StepBottom = styled.div `
  padding-top: ${fluid(28, 40)};
  display: flex;
  flex-wrap: wrap;
  gap: 16px ${fluid(24, 56)};
  align-items: center;

  ${CtaLinkStyles.Container} {
    font-size: ${fluid(18, 20)};
    line-height: ${fluid(22, 26)};

    svg {
      width: 14px;
      order: -1;
      transform: scale(-1);
      margin: 0 8px 0 0;
    }

    &:focus-visible,
    &:hover {
      svg {
        transform: scale(-1) translateX(${fluid(3, 5)}) !important;
      }
    }
  }
`;
const SplitFields = styled.div `
  display: grid;
  column-gap: 16px;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: 1fr 1fr;
  }
`;
const fieldBase = css `
  border-radius: 100px;
  font: inherit;
  color: ${brand.base.blue};
  height: 48px;
  padding: 0 24px;
  ${paragraphSmall};
  margin: 0;
  border: 2px solid ${brand.secondary.blueGrey};
  background-color: ${brand.white};

  &[readonly],
  &[disabled] {
    --backgroundDisabled: ${brand.white};

    opacity: 0.5;
  }

  &::placeholder {
    font: inherit;
    color: ${transparentize(0.5, brand.base.blue)};
  }
`;
export const labelBase = css `
  ${paragraphSmall};
  ${fonts.lato.semiBold};

  display: block;
  margin: 0 0 8px;
  color: ${brand.base.blue};
`;
const Field = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${FieldSharedStyles.Container} {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  ${FieldSharedStyles.Label} {
    ${labelBase};

    flex: 0;
  }

  input,
  select,
  textarea {
    ${fieldBase}
  }

  ${RadioBtnStyles.RadioBtnText}::before {
    border-color: ${brand.secondary.blueGrey};
    background-color: ${brand.white};
  }

  select {
    option:first-of-type {
      color: ${transparentize(0.5, brand.base.blue)};
    }

    option {
      color: ${brand.base.blue};
    }
  }

  textarea {
    padding: 15px 24px;
    border-radius: 30px;
  }

  &[data-promo] {
    flex-direction: row;
    width: 100%;
    max-width: 608px;

    ${FieldSharedStyles.Container} {
      flex-grow: 1;
    }

    ${ButtonStyles.Container} {
      margin-top: calc(${fluid(26, 28)} + 8px);
      height: 48px;
    }

    @media ${until(Device.Tablet)} {
      ${ButtonStyles.Text} {
        span {
          display: none;
        }
      }
    }
  }
`;
const ExternalLabel = styled.label `
  ${labelBase};

  margin-bottom: 16px;
`;
const FormError = styled.p `
  ${paragraphMedium};

  color: ${brand.validation.invalid};
`;
export const BookEventFormStyles = {
    Container,
    Layout,
    SectionTitle,
    EventType,
    LeftContent,
    Title,
    InPartnershipWith,
    HR,
    DetailsList,
    Details,
    FormAccordion,
    FormBase,
    FormStepTitle,
    FormContent,
    FormContentInner,
    StepBottom,
    SplitFields,
    Field,
    ExternalLabel,
    FormError,
    AlternativeContent,
};
